import React, { useMemo } from 'react';
import { Controller, SubmitHandler, useFormContext } from 'react-hook-form';
import {
  Grid, Typography, Box,
} from '@mui/material';
import {
  ErrorMessage,
  FileUploader,
  PrimaryLiteButton,
  PrimaryLoadingButton,
  PrimaryTextField,
} from 'components';
import { CharacterCountDisplay } from 'components/molecules/primary-text-field';
import { useLocation } from 'react-router-dom';

import { UploadResponse, UploadScenarios } from 'interfaces/file-uploader';
import { doesPathMatch } from 'utils/data-utils';
import { useSelector } from 'react-redux';
import { selectPlugins } from 'pages/dashboard/components/ent-account/components/edit-account/components/plugins/components/view-plugins/selector';
import { IBadge } from '../../../../../interface';
import SessionPlugins from '../../session-details/components/session-plugins';
import { badgeFieldConfigs } from '../validation/badge-modal-form-validation';

interface BadgeModalFormProps {
  title:string,
  onSubmit: SubmitHandler<any>;
  closeModal: () => void;
  loading:boolean;
}

const BadgeModalForm = ({
  title,
  onSubmit,
  closeModal, loading,
}: BadgeModalFormProps) => {
  const {
    control, handleSubmit,
    setValue, getValues, clearErrors, setError,
    formState: { errors },
  } = useFormContext<IBadge>();

  const handleUrl = (res:UploadResponse) => {
    clearErrors('badgeImgUrl');
    setValue('badgeImgUrl', res.url);
    if (res.statusCode !== 201) {
      setError('badgeImgUrl', { type: 'custom', message: res.message });
    }
  };
  const handleUrlRemove = () => {
    setValue('badgeImgUrl', '');
  };
  const handleForm = (event:any) => {
    event.preventDefault();
    event.stopPropagation();
    handleSubmit(onSubmit)();
  };

  const location = useLocation();

  const plugins = useSelector(selectPlugins);

  const activePlugins = useMemo(
    () => plugins.filter((i) => i.isActive && doesPathMatch(location.pathname, i.components)),
    [plugins, location.pathname],
  );

  const isAnyPluginActive = useMemo(
    () => plugins.some((i) => i.isActive && doesPathMatch(location.pathname, i.components)),
    [plugins, location.pathname],
  );

  return (
    <form onSubmit={handleForm}>
      <Typography variant="h4" className="form-item">
        {title}
      </Typography>
      <div className="form-item">
        <Typography variant="h4">Course badge</Typography>
        <FileUploader
          handleFile={handleUrl}
          handleRemove={handleUrlRemove}
          currentContentUrl={getValues('badgeImgUrl')}
          scenario={UploadScenarios.expertBadge}
          currentMediaType="image"
        />
        <ErrorMessage>{errors?.badgeImgUrl?.message}</ErrorMessage>
      </div>
      <div className="form-item">
        <Controller
          control={control}
          name="badgeTitle"
          render={({ field, fieldState }) => (
            <>
              <PrimaryTextField {...field} label="Badge title" />
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 0.5,
              }}
              >
                <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                <CharacterCountDisplay value={field.value || ''} config={badgeFieldConfigs.title} />
              </Box>
            </>
          )}
        />
      </div>
      <div className="form-item">
        <Controller
          control={control}
          name="badgeDescription"
          render={({ field, fieldState }) => (
            <>
              <PrimaryTextField {...field} multiline label="Badge description" />
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 0.5,
              }}
              >
                <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                <CharacterCountDisplay value={field.value || ''} config={badgeFieldConfigs.description} />
              </Box>
            </>
          )}
        />
      </div>

      {isAnyPluginActive && <SessionPlugins plugins={activePlugins} context="badge" />}

      <Grid container spacing={3}>
        <Grid item xs>
          <PrimaryLiteButton fullWidth onClick={closeModal}>Cancel</PrimaryLiteButton>
        </Grid>
        <Grid item xs>
          <PrimaryLoadingButton loading={loading} fullWidth type="submit">
            Add & continue
          </PrimaryLoadingButton>
        </Grid>

      </Grid>
    </form>
  );
};

export default BadgeModalForm;
